import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';

const STEPS = [
  'Analyzing Bangladesh city listing data...',
  'Computing median price movements in BDT...',
  'Evaluating YoY growth trends across divisions...',
  'Reviewing RAJUK regulatory compliance updates...',
  'Assessing new residential project launches...',
  'Analyzing NRB/expat buying patterns...',
  'Compiling investment hotspot rankings...',
  'Generating executive summary...',
];

const BDGenerationStatus = ({ isGenerating, city, period }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isGenerating) {
      setCurrentStep(0);
      setElapsed(0);
      return;
    }
    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => (prev < STEPS?.length - 1 ? prev + 1 : prev));
    }, 2200);
    const timeInterval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => {
      clearInterval(stepInterval);
      clearInterval(timeInterval);
    };
  }, [isGenerating]);

  if (!isGenerating) return null;

  const progress = Math.round(((currentStep + 1) / STEPS?.length) * 100);
  const estimatedTotal = Math.round(STEPS?.length * 2.2);
  const remaining = Math.max(0, estimatedTotal - elapsed);

  return (
    <div>
      <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-green-900 text-sm">OpenAI is generating your Bangladesh report</p>
            <p className="text-green-600 text-xs truncate">{city} · {period} · Est. {remaining}s remaining</p>
          </div>
          <div className="ml-auto text-green-700 font-bold text-lg tabular-nums">{progress}%</div>
        </div>
        <div className="w-full bg-green-200 rounded-full h-2.5 mb-3 overflow-hidden">
          <div
            className="h-2.5 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #16a34a, #22c55e, #4ade80)' }}
          />
        </div>
        <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1">
          {STEPS?.map((_, i) => (
            <div
              key={i}
              className="flex-shrink-0 h-1.5 rounded-full transition-all duration-500"
              style={{
                width: i <= currentStep ? '20px' : '8px',
                background: i < currentStep ? '#16a34a' : i === currentStep ? '#22c55e' : '#bbf7d0',
              }}
            />
          ))}
        </div>
        <div className="flex items-center gap-2 text-green-700 text-xs">
          <Icon name="Loader" size={12} className="animate-spin" />
          <span className="font-medium">{STEPS?.[currentStep]}</span>
        </div>
      </div>
      {/* Skeleton */}
      <div className="space-y-4">
        {[1, 2, 3]?.map((i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
            <div className="space-y-2">
              <div className="h-3 bg-gray-100 rounded w-full" />
              <div className="h-3 bg-gray-100 rounded w-5/6" />
              <div className="h-3 bg-gray-100 rounded w-4/6" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BDGenerationStatus;
